import React, { useState, useEffect } from 'react';
import { Table, Card, Tag, Space, Button, DatePicker, message, Empty, Spin } from 'antd';
import { ReloadOutlined, FilterOutlined } from '@ant-design/icons';
import axiosInstance from '../../services/axiosConfig';
import { INVOICE_API } from '../../constants/api';
import moment from 'moment';

const { RangePicker } = DatePicker;

const PaymentHistory = ({ contractId, contract, paymentData }) => {
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);

  // Debug log để kiểm tra dữ liệu nhận được từ props
  useEffect(() => {
    console.log('PaymentHistory Component - Props nhận được:');
    console.log('contractId:', contractId);
    console.log('contract:', contract);
    console.log('paymentData:', paymentData);
  }, [contractId, contract, paymentData]);

  // Format tiền tệ VND
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', { 
      style: 'currency', 
      currency: 'VND' 
    }).format(amount);
  };

  // Format ngày giờ
  const formatDate = (dateString) => {
    if (!dateString) return '--';
    return moment(dateString).format('DD/MM/YYYY HH:mm');
  };

  // Lấy dữ liệu lịch sử thanh toán
  const fetchPaymentHistory = async () => {
    // Nếu đã có dữ liệu sẵn từ prop, sử dụng luôn
    if (paymentData && Array.isArray(paymentData)) {
      setPayments(paymentData);
      return;
    }

    // Nếu không có dữ liệu sẵn, gọi API để lấy
    const id = contractId || (contract && contract.ContractID);
    if (!id) return;

    setLoading(true);
    try {
      const response = await axiosInstance.get(INVOICE_API.CONTRACT_PAYMENT_HISTORY(id));
      setPayments(response.data || []);
    } catch (error) {
      console.error('Lỗi khi tải lịch sử thanh toán:', error);
      message.error('Không thể tải lịch sử thanh toán');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Nếu có dữ liệu thanh toán truyền trực tiếp qua prop
    if (paymentData && Array.isArray(paymentData)) {
      setPayments(paymentData);
    } 
    // Nếu không, gọi API để lấy dữ liệu
    else {
      fetchPaymentHistory();
    }
  }, [contractId, contract, paymentData]);

  // Lọc theo ngày thanh toán
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  // Lọc dữ liệu theo ngày
  const getFilteredData = () => {
    if (!dateRange[0] || !dateRange[1]) {
      return payments;
    }

    return payments.filter(payment => {
      const paymentDate = moment(payment.PaymentDate);
      return paymentDate.isBetween(dateRange[0], dateRange[1], null, '[]');
    });
  };

  const columns = [
    {
      title: 'Mã hóa đơn',
      dataIndex: 'InvoiceID',
      key: 'InvoiceID',
    },
    {
      title: 'Ngày thanh toán',
      dataIndex: 'PaymentDate',
      key: 'PaymentDate',
      render: (date) => formatDate(date),
      sorter: (a, b) => new Date(a.PaymentDate) - new Date(b.PaymentDate),
    },
    {
      title: 'Số tiền',
      dataIndex: 'Amount',
      key: 'Amount',
      render: (amount) => formatCurrency(amount),
      sorter: (a, b) => a.Amount - b.Amount,
    },
    {
      title: 'Phương thức',
      dataIndex: 'PaymentMethod',
      key: 'PaymentMethod',
    },
    
    {
      title: 'Kỳ thanh toán',
      dataIndex: 'Period',
      key: 'Period',
      
    },
    {
      title: 'Ghi chú',
      dataIndex: 'Note',
      key: 'Note',
      ellipsis: true,
    },
  ];

  const filteredData = getFilteredData();

  return (
    <Card 
      title="Lịch sử thanh toán"
      extra={
        <Space>
          <RangePicker 
            onChange={handleDateRangeChange}
            format="DD/MM/YYYY"
            placeholder={['Từ ngày', 'Đến ngày']}
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchPaymentHistory}
            loading={loading}
          >
            Làm mới
          </Button>
        </Space>
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {filteredData.length > 0 ? (
            <Table 
              dataSource={filteredData} 
              columns={columns}
              rowKey="PaymentID"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50'],
              }}
            />
          ) : (
            <Empty description="Không có dữ liệu thanh toán" />
          )}
        </>
      )}
    </Card>
  );
};

export default PaymentHistory; 